<template>
  <Layout v-if="wait.is('Loading List')">
    <div class="layout__wrapper">
      <LoadingSpinner />
    </div>
  </Layout>

  <Layout v-else-if="view" class="layout--basic">
    <HeaderInternal
      :title="view.pageTitle()"
      :class="view.isHeaderCollapsed ? 'header--scroll' : 'header--default'"
    >
      <template #actions>
        <DocsSortButtonMobile v-if="view.docs().isDocSortVisible()" />
        <CustomListMenu v-if="showCustomListMenu" :list="view.customList" />
      </template>
    </HeaderInternal>

    <div class="layout__wrapper">
      <BackLink class="docs-listing__backlink container" />

      <component
        :is="listPageDescriptionComponent()"
        v-if="view.descriptionView"
        :view="view.descriptionView"
      />

      <section class="docs-listing" :class="sectionCssClass()">
        <div class="layout__wrapper-inner container">
          <ListDocs :view="view" />
        </div>
      </section>
    </div>
  </Layout>

  <Layout v-else-if="!view && wait.is('Error Loading List')">
    <div class="layout__wrapper">
      <LoadingError />
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, Raw, ref } from 'vue'
import { useRouter } from 'vue-router'

import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import LoadingError from '@/components/ui/LoadingError.vue'

import Layout from '@/app/components/Layout.vue'
import BackLink from '@/app/components/BackLink.vue'
import HeaderInternal from '@/app/components/header/Internal.vue'
import ListDocs from '@/app/components/listing/ListDocs.vue'
import ListingPageDescription from '@/app/components/listing/ListingPageDescription.vue'
import ListingPageDescriptionPodcast from '@/app/components/listing/ListingPageDescriptionPodcast.vue'

import DocsSortButtonMobile from '@/app/components/listing/DocsSortButton.mobile.vue'
import CustomListMenu from '@/app/components/custom.lists/CustomListMenu.vue'

import { createListViewByRoute, ListView } from '@/models/docs.list'
import { useWait } from '@/app/composables/useWait'
import { Sentry } from '@/services/sentry'
import { ListItemType } from '@/models/interfaces'

const view = ref<Raw<ListView> | null>(null)

const wait = useWait()
const router = useRouter()

onBeforeMount(async () => {
  await initView()
})

async function initView(): Promise<void> {
  wait.start('Loading List')
  try {
    const route = router.currentRoute.value
    const routeName = route.name as string
    const params = route.params

    view.value = await createListViewByRoute(routeName, params)
    wait.end('Loading List')
  } catch (error: any) {
    console.error(error)
    wait.start('Error Loading List')
    Sentry.captureException(error)
  }
}

function listPageDescriptionComponent(): any {
  if (view.value?.descriptionView?.doc_type === 'podcast') {
    return ListingPageDescriptionPodcast
  }
  return ListingPageDescription
}

/**
 * Return whether or not the current page is a non-standard Custom List listing page.
 */
const showCustomListMenu = computed(() => {
  if (!view.value?.customList) {
    return false
  }

  const isCustomList = view.value.customList.doc_type === 'custom_list'
  const isStandard = view.value.customList.is_standard

  return isCustomList && !isStandard
})

type SectionCssType = {
  [key in ListItemType]: string
}

const sectionCss: SectionCssType = {
  article: 'docs-listing--articles',
  book: 'docs-listing--books',
  podcast_episode: 'docs-listing--podcast-episodes',
  podcast: 'docs-listing--podcasts',
  collection: 'docs-listing--collections',
  custom_list: 'docs-listing--custom-lists',
}

function sectionCssClass(): string | void {
  if (!view.value) {
    return
  }
  return sectionCss[view.value.docTypeName()]
}
</script>

<style lang="scss" scoped>
:deep(.description-box) {
  margin-top: 40px;
  @media only screen and (max-width: $w-tablet-s) {
    margin-top: 0;
  }
}

.docs-listing {
  &__backlink {
    @media only screen and (max-width: $w-tablet-s) {
      display: none;
    }
  }

  &__list {
    --docs-listing-columns-desktop: 4;
    display: grid;
    grid-template-columns: repeat(var(--docs-listing-columns-desktop), 1fr);
    gap: 24px;
    @media only screen and (max-width: $w-tablet-s) {
      grid-template-columns: 1fr;
      gap: 16px;
    }
    @media only screen and (max-width: $w-tablet-s) {
      gap: 13px;
    }
  }

  &__backlink {
    margin-top: 24px;
  }
}

.docs-listing--articles .docs-listing__list {
  --docs-listing-columns-desktop: 3;
}

.docs-listing--podcast-episodes {
  :deep(.docs-listing__list) {
    --docs-listing-columns-desktop: 3;
  }

  :deep(.header.header--internal) {
    transition: all 0.2s;
  }

  :deep(.header--internal) {
    padding-bottom: 14px;
  }

  :deep(.description__name) {
    @media only screen and (max-width: $w-tablet-s) {
      display: block;
    }
  }
}

.docs-listing--collections .docs-listing {
  &__list {
    @media (max-width: $w-tablet-s) {
      grid-template-columns: repeat(auto-fit, minmax(235px, 1fr));
      overflow: visible;
    }
  }
}
</style>
